import { lazy, Suspense } from 'react'

import { useRouteMatch } from 'react-router'

import { Page } from '@webapp/core/components/Page'
import { useQueryCustomField } from '@webapp/core/features/get-custom-fields/models/useQueryCustomField'
import { useAppRoutes, useProcurifyIntl } from '@webapp/hooks'
import {
  MainCustomFieldSetupPageTabs,
  type MainCustomFieldSetupPageTab,
} from './MainCustomFieldSetupPage'
import messages from './messages'

export interface MainCustomFieldDetailPageParams {
  tab: MainCustomFieldSetupPageTab
  id: string
}

const CustomFieldDetailViewChunk = lazy(() =>
  import('@webapp/platform/custom-fields/feature-sets/CustomFieldDetailView').then(
    ({ CustomFieldDetailView }) => ({
      default: CustomFieldDetailView,
    })
  )
)

export const MainCustomFieldDetailPage = () => {
  const intl = useProcurifyIntl()
  const { url } = useAppRoutes()
  const { params } = useRouteMatch<MainCustomFieldDetailPageParams>()

  const customFieldId = Number(params.id)
  const fieldKind =
    params.tab === MainCustomFieldSetupPageTabs.HEADER_FIELDS
      ? 'header'
      : 'lineItem'

  // Fetched here purely to name the breadcrumb; the view fetches its own copy.
  const { customField } = useQueryCustomField({ customFieldId })

  return (
    <Page
      pageTitle={intl.formatMessage(messages.mainCustomFieldSetupPageTitle)}
      breadcrumbs={[
        {
          link: url('SettingsPage'),
          name: intl.formatMessage(messages.settingsPageTitle),
        },
        {
          link: url('MainCustomFieldSetupPage', { tab: params.tab }),
          name: intl.formatMessage(messages.mainCustomFieldSetupPageTitle),
        },
        {
          name: customField?.name ?? '',
          escapeCaseFormatting: true,
        },
      ]}
    >
      <Suspense fallback={null}>
        <CustomFieldDetailViewChunk
          customFieldId={customFieldId}
          fieldKind={fieldKind}
        />
      </Suspense>
    </Page>
  )
}

MainCustomFieldDetailPage.displayName = 'MainCustomFieldDetailPage'
